// 본문 예약 내부 링크 토큰 — `[[internal:{키}|표시 문구]]`.
//
// 키는 대상 글의 정규 공개 경로 조각을 점으로 이은 값(`faq.두통.편두통.slug`,
// `blog.my-post`). 토큰은 body_json 의 일반 텍스트로 저장되고, 편집기는 칩으로
// 보여 주며, 공개 렌더러는 발행 원장의 경로와 대조해 링크로 바꾼다. 여기 정규식
// 하나가 편집기·렌더러·FRONT 예제의 SSoT 다(어드민 `buildInternalContentLinkToken`
// 이 만드는 형식과 같다).

export const INTERNAL_CONTENT_LINK_TOKEN_SOURCE = String.raw`\[\[internal:([a-z0-9가-힣]+(?:[.-][a-z0-9가-힣]+)*)\|([^\]\n|]+)\]\]`;

export interface InternalContentLinkToken {
  /** 토큰이 시작하는 문자 인덱스(텍스트 기준). */
  readonly index: number;
  /** 원문 토큰 전체(`[[internal:…|…]]`). */
  readonly raw: string;
  /** 소문자로 정규화한 키. */
  readonly key: string;
  /** 표시 문구(앞뒤 공백 제거). */
  readonly label: string;
}

/** 텍스트 안의 토큰을 앞에서부터 모두 찾는다. 겹치지 않는다. */
export function findInternalContentLinkTokens(
  text: string,
): InternalContentLinkToken[] {
  const pattern = new RegExp(INTERNAL_CONTENT_LINK_TOKEN_SOURCE, "giu");
  const tokens: InternalContentLinkToken[] = [];
  for (const match of text.matchAll(pattern)) {
    const [raw, key, label] = match;
    if (!key || !label || match.index === undefined) continue;
    tokens.push({
      index: match.index,
      raw,
      key: key.toLowerCase(),
      label: label.trim(),
    });
  }
  return tokens;
}

/** 공개 경로(`/faq/a/b/slug`) → 키(`faq.a.b.slug`). 조각이 없으면 null. */
export function internalContentKeyFromPath(
  path: string | null | undefined,
): string | null {
  if (!path) return null;
  const segments = path
    .split("/")
    .filter(Boolean)
    .map((segment) => {
      try {
        return decodeURIComponent(segment).trim().toLowerCase();
      } catch {
        return "";
      }
    });
  if (
    segments.length === 0 ||
    segments.some((segment) => segment === "" || segment.includes("."))
  ) {
    return null;
  }
  return segments.join(".");
}

/** 키 → 공개 경로. 없으면 null(문구만 표시). */
export type InternalContentLinkResolver = (key: string) => string | null;

interface InternalContentPathEntry {
  /** 저장된 정규 공개 경로(`/faq/a/b/slug`). 없으면 대상이 아니다. */
  readonly path?: string | null;
  /** 옛 slug 목록 — 옛 키로 남은 본문도 현재 주소로 이어 준다. */
  readonly previousSlugs?: readonly string[] | null;
}

/**
 * 발행 글 목록에서 "키 → 현재 경로" 색인을 만든다. 옛 slug 도 같은 글로 등록한다
 * (경로 마지막 조각을 옛 slug 로 바꾼 키). 현재 경로가 옛 키와 겹치면 현재가 이긴다.
 */
export function internalContentPathIndex(
  entries: Iterable<InternalContentPathEntry>,
): Map<string, string> {
  const index = new Map<string, string>();
  const previous = new Map<string, string>();
  for (const entry of entries) {
    const path = entry.path;
    const key = internalContentKeyFromPath(path);
    if (!path || !key) continue;
    index.set(key, path);
    const segments = key.split(".");
    for (const oldSlug of entry.previousSlugs ?? []) {
      const oldKey = [...segments.slice(0, -1), oldSlug.trim().toLowerCase()].join(".");
      if (oldKey && !oldKey.includes("..")) previous.set(oldKey, path);
    }
  }
  for (const [key, path] of previous) {
    if (!index.has(key)) index.set(key, path);
  }
  return index;
}

/** Map 이나 함수 어느 쪽이든 resolver 로. */
export function internalContentLinkResolverFrom(
  source: InternalContentLinkResolver | ReadonlyMap<string, string> | null | undefined,
): InternalContentLinkResolver {
  if (!source) return () => null;
  if (typeof source === "function") return source;
  return (key) => source.get(key.toLowerCase()) ?? null;
}

function isSafeInternalPath(path: string | null): path is string {
  return typeof path === "string" && path.startsWith("/") && !path.startsWith("//");
}

function escapeHtmlText(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

/** 링크로 렌더된 토큰의 class — 사이트 CSS 가 잡을 고리. */
export const INTERNAL_CONTENT_LINK_CLASS = "rt-internal-link";
/** 대상이 아직 없어 문구만 남긴 토큰의 class·data 속성. */
export const INTERNAL_CONTENT_LINK_PENDING_CLASS = "rt-internal-link--pending";
export const INTERNAL_CONTENT_LINK_PENDING_ATTR = "data-rt-internal-link-pending";

/**
 * 이미 이스케이프된 텍스트 한 구간의 토큰을 HTML 로 바꾼다. 텍스트 부분은 다시
 * 이스케이프하지 않는다(정화기가 이미 처리한 값). 토큰이 없으면 원문 그대로.
 */
export function renderInternalContentLinksInText(
  text: string,
  resolve: InternalContentLinkResolver,
): { readonly html: string; readonly hasTokens: boolean } {
  const tokens = findInternalContentLinkTokens(text);
  if (tokens.length === 0) return { html: text, hasTokens: false };
  let cursor = 0;
  let html = "";
  for (const token of tokens) {
    html += text.slice(cursor, token.index);
    const path = resolve(token.key);
    const label = escapeHtmlText(token.label);
    html += isSafeInternalPath(path)
      ? `<a href="${escapeHtmlText(path)}" class="${INTERNAL_CONTENT_LINK_CLASS}">${label}</a>`
      : `<span class="${INTERNAL_CONTENT_LINK_CLASS} ${INTERNAL_CONTENT_LINK_PENDING_CLASS}" ${INTERNAL_CONTENT_LINK_PENDING_ATTR}="${escapeHtmlText(token.key)}">${label}</span>`;
    cursor = token.index + token.raw.length;
  }
  html += text.slice(cursor);
  return { html, hasTokens: true };
}

const INTERNAL_LINK_BLOCKED_HTML_TAGS = new Set([
  "a",
  "code",
  "pre",
  "script",
  "style",
  "textarea",
]);
const VOID_HTML_TAGS = new Set(["br", "hr", "img", "input", "source", "wbr"]);

function htmlTagEnd(html: string, start: number): number {
  let quote: '"' | "'" | null = null;
  for (let index = start + 1; index < html.length; index += 1) {
    const character = html[index];
    if (quote) {
      if (character === quote) quote = null;
      continue;
    }
    if (character === '"' || character === "'") {
      quote = character;
      continue;
    }
    if (character === ">") return index;
  }
  return -1;
}

/**
 * 정화된 HTML(원문 보존 블록 등)의 텍스트 구간에만 토큰을 적용한다. `<a>`·`<code>`·
 * `<pre>` 안과 태그 속성값은 건드리지 않는다.
 */
export function renderInternalContentLinksInHtml(
  html: string,
  resolve: InternalContentLinkResolver,
): { readonly html: string; readonly hasTokens: boolean } {
  if (!html.includes("[[internal:")) return { html, hasTokens: false };
  let blockedDepth = 0;
  let cursor = 0;
  let out = "";
  let hasTokens = false;
  const emitText = (text: string) => {
    if (blockedDepth > 0 || !text) {
      out += text;
      return;
    }
    const rendered = renderInternalContentLinksInText(text, resolve);
    hasTokens ||= rendered.hasTokens;
    out += rendered.html;
  };
  while (cursor < html.length) {
    const tagStart = html.indexOf("<", cursor);
    if (tagStart < 0) {
      emitText(html.slice(cursor));
      break;
    }
    emitText(html.slice(cursor, tagStart));
    const tagEnd = htmlTagEnd(html, tagStart);
    if (tagEnd < 0) {
      out += html.slice(tagStart);
      break;
    }
    const tag = html.slice(tagStart, tagEnd + 1);
    out += tag;
    const parsed = /^<\s*(\/?)\s*([a-z0-9-]+)/i.exec(tag);
    const tagName = parsed?.[2]?.toLowerCase();
    if (tagName && INTERNAL_LINK_BLOCKED_HTML_TAGS.has(tagName)) {
      const isClosing = parsed?.[1] === "/";
      const isVoid = VOID_HTML_TAGS.has(tagName) || /\/\s*>$/.test(tag);
      if (isClosing) blockedDepth = Math.max(0, blockedDepth - 1);
      else if (!isVoid) blockedDepth += 1;
    }
    cursor = tagEnd + 1;
  }
  return { html: out, hasTokens };
}
